import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { trpc } from "@/lib/trpc";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { Loader2, TrendingUp, Clock, Calendar, Activity } from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";

export default function GymStatistics() {
  const { data: peakHours, isLoading: loadingPeakHours } = trpc.gym.statistics.peakHours.useQuery();
  const { data: usageByDay, isLoading: loadingUsageByDay } = trpc.gym.statistics.usageByDay.useQuery();
  const { data: servicePopularity, isLoading: loadingServicePopularity } = trpc.gym.statistics.servicePopularity.useQuery();

  const COLORS = ['#7AB51D', '#F7941D', '#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

  if (loadingPeakHours || loadingUsageByDay || loadingServicePopularity) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  const totalCheckIns = peakHours?.reduce((sum, h) => sum + h.count, 0) || 0;
  const peakHour = peakHours?.reduce((max, h) => h.count > max.count ? h : max, peakHours[0]);
  const mostPopularDay = usageByDay?.reduce((max, d) => d.count > max.count ? d : max, usageByDay[0]);
  const mostPopularService = servicePopularity?.[0];

  return (
    <DashboardLayout>
      <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Gym Statistiken</h1>
        <p className="text-muted-foreground mt-2">
          Analysieren Sie Nutzungsmuster und optimieren Sie Ihr Angebot
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Gesamt Check-ins</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalCheckIns}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Alle erfassten Check-ins
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Stoßzeit</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{peakHour?.label || "-"}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {peakHour?.count || 0} Check-ins
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Beliebtester Tag</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{mostPopularDay?.dayName || "-"}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {mostPopularDay?.count || 0} Check-ins
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Top Service</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold truncate">{mostPopularService?.serviceName || "-"}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {mostPopularService?.bookingCount || 0} Buchungen
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Peak Hours Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Stoßzeiten nach Uhrzeit</CardTitle>
          <CardDescription>
            Anzahl der Check-ins pro Stunde des Tages
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={peakHours}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="count" fill="#7AB51D" name="Check-ins" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Usage by Day of Week */}
        <Card>
          <CardHeader>
            <CardTitle>Nutzung nach Wochentag</CardTitle>
            <CardDescription>
              Verteilung der Check-ins über die Woche
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={usageByDay}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="dayName" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#F7941D" name="Check-ins" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Service Popularity */}
        <Card>
          <CardHeader>
            <CardTitle>Beliebteste Services</CardTitle>
            <CardDescription>
              Verteilung der Buchungen nach Service-Typ
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={servicePopularity}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={(entry) => entry.serviceName}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="bookingCount"
                >
                  {servicePopularity?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Service Popularity Table */}
      <Card>
        <CardHeader>
          <CardTitle>Service-Ranking</CardTitle>
          <CardDescription>
            Detaillierte Übersicht der Service-Beliebtheit
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {servicePopularity?.map((service, index) => (
              <div key={service.serviceId} className="flex items-center">
                <div className="flex items-center gap-3 flex-1">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-semibold text-sm">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">{service.serviceName}</p>
                    <p className="text-sm text-muted-foreground">{service.bookingCount} Buchungen</p>
                  </div>
                </div>
                <div className="w-32">
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary transition-all"
                      style={{
                        width: `${(service.bookingCount / (servicePopularity[0]?.bookingCount || 1)) * 100}%`
                      }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
      </div>
    </DashboardLayout>
  );
}
